import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Breadcrumb,
  Button,
  Input,
  InputNumber,
  message,
  PageHeader,
  Select,
  Form,
  Row,
  Col,
  DatePicker,
  Table,
  Typography,
  Popconfirm,
  Space,
} from "antd";
import moment from "moment";
import Page from "_components/Page";
import {
  companyService,
  materialTypeService,
  materialRequestService,
  measurementTypeService,
  stockClassificationService,
} from "_services";
import { Utils } from "_helpers";
import { CloseOutlined, PlusOutlined } from "@ant-design/icons";
import { v4 as uuidv4 } from "uuid";

let lineIndex = 0;

function Add({ history, match }) {
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCustomers, setIsLoadingCustomers] = useState(false);
  const [isLoadingStores, setIsLoadingStores] = useState(false);
  const [isLoadingCustomerAddresses, setIsLoadingCustomerAddresses] =
    useState(false);
  const [isLoadingStoreAddresses, setIsLoadingStoreAddresses] = useState(false);
  const [isLoadingStockClassification, setIsLoadingStockClassification] =
    useState(false);
  const [stockClassifications, setStockClassifications] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerAddresses, setCustomerAddresses] = useState([]);
  const [stores, setStores] = useState([]);
  const [storeAddresses, setStoreAddresses] = useState([]);
  const [materialTypes, setMaterialTypes] = useState([]);
  const [materialRequestLines, setMaterialRequestLines] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [measurementTypes, setMeasurementTypes] = useState([]);
  const [error, setError] = useState(null);
  const [form] = Form.useForm();

  const { Option } = Select;
  const { Text } = Typography;
  const { TextArea } = Input;

  useEffect(() => {
    getCustomerCompanies();
    getStoreCompanies();
    // getMaterialTypes();
    getMeasurementTypes();
    getStockClassificationSuggestion();
  }, []);

  const getCustomerCompanies = () => {
    setIsLoadingCustomers(true);
    companyService
      .getSuggestion("?companyTypeCodes=OWN,MAF")
      .then((x) => {
        setIsLoadingCustomers(false);
        setCustomers(x.content);
      })
      .catch((errorInfo) => {
        setIsLoadingCustomers(false);
        message.error(`${errorInfo}`);
      });
  };

  const getStockClassificationSuggestion = () => {
    setIsLoadingStockClassification(true);
    stockClassificationService
      .getSuggestion()
      .then((x) => {
        setIsLoadingStockClassification(false);
        setStockClassifications(x.content);
      })
      .catch((errorInfo) => {
        setIsLoadingStockClassification(false);
        message.error(`${errorInfo}`);
      });
  };

  const getStoreCompanies = () => {
    setIsLoadingStores(true);
    companyService
      .getSuggestion("?companyTypeCodes=STORE")
      .then((x) => {
        setIsLoadingStores(false);
        setStores(x.content);
      })
      .catch((errorInfo) => {
        setIsLoadingStores(false);
        message.error(`${errorInfo}`);
      });
  };

  const getCustomerAddresses = (companyId) => {
    if (companyId) {
      setCustomerAddresses([]);
      form.resetFields(["deliveryLocation"]);
      setIsLoadingCustomerAddresses(true);
      companyService
        .getById(companyId)
        .then((x) => {
          setIsLoadingCustomerAddresses(false);
          setCustomerAddresses(x.content.addresses);
        })
        .catch((errorInfo) => {
          setIsLoadingCustomerAddresses(false);
        });
    }
  };

  const getStoreAddresses = (companyId) => {
    if (companyId) {
      setStoreAddresses([]);
      form.resetFields(["storeAddress"]);
      setIsLoadingStoreAddresses(true);
      companyService
        .getById(companyId)
        .then((x) => {
          setIsLoadingStoreAddresses(false);
          setStoreAddresses(x.content.addresses);
        })
        .catch((errorInfo) => {
          setIsLoadingStoreAddresses(false);
        });
    }
  };

  const getMaterialTypesBasedOnClassification = () => {
    const classificationId = form.getFieldValue(["stockClass", "id"]);
    if (!classificationId) {
      message.error("Please select a Stock Classification first");
      return;
    }
    materialTypeService
      .getSuggestion(`?stockClassId=${classificationId}`)
      .then((x) => {
        setMaterialTypes(x.content);
      })
      .catch((errorInfo) => {
        message.error(`${errorInfo}`);
      });
  };

  const getToCompanyAddresses = (companyId) => {
    form.resetFields(["deliveryLocation"]);

    companyService.getById(companyId).then((x) => {
      setCustomerAddresses(x.content.addresses);
    });
  };

  const getMaterialTypes = () => {
    materialTypeService
      .getSuggestion("")
      .then((x) => {
        setMaterialTypes(x.content);
      })
      .catch((errorInfo) => {
        message.error(`${errorInfo}`);
      });
  };

  const getMeasurementTypes = () => {
    measurementTypeService
      .getSuggestion()
      .then((x) => {
        setMeasurementTypes(x.content);
      })
      .catch((errorInfo) => {
        message.error(`${errorInfo}`);
      });
  };

  const handleFieldChange = (value, fieldName, key, measurementIndex) => {
    const lines = [...materialRequestLines];
    const newRequestLines = lines.map((line) => {
      const newLine = Object.assign({}, line);
      if (newLine.key === key) {
        if (fieldName === "stockType") {
          newLine[fieldName] = materialTypes.find((item) => item.id === value);
          return newLine;
        } else if (
          fieldName === "measurementType" ||
          fieldName === "measurementValue"
        ) {
          if (!newLine.measurements || !Array.isArray(newLine.measurements)) {
            console.error("Measurements array is missing or invalid");
            return newLine;
          }

          if (fieldName === "measurementType") {
            if (
              hasDuplicateMeasurementType(
                newLine.measurements,
                value,
                measurementIndex
              )
            ) {
              message.error("Duplicate measurement type is not allowed!");
              return newLine; // Skip the update
            }
          }

          const updatedMeasurements = newLine.measurements.map(
            (measurement, index) => {
              if (index === measurementIndex) {
                return {
                  ...measurement,
                  [fieldName]: value,
                };
              }
              return measurement;
            }
          );

          return {
            ...newLine,
            measurements: updatedMeasurements,
          };
        } else {
          newLine[fieldName] = value;
          return newLine;
        }
      }
      return newLine;
    });
    setMaterialRequestLines(newRequestLines);
  };

  /* const handleAddRequestLine = () => {
    const defaultMeasurementType = measurementTypes.find(
      (item) => item.name === "NOS"
    );

    const newLine =
      defaultMeasurementType === null
        ? { requestedItems: [] }
        : {
            requestedItems: [],
            measurementType: defaultMeasurementType.id,
          };
    newLine.key = lineIndex;
    const lines = [...materialRequestLines, newLine];
    setMaterialRequestLines(lines);
    lineIndex += 1;
  }; */

  const handleAddRequestLine = () => {
    const defaultMeasurementType = measurementTypes.find(
      (item) => item.name === "NOS"
    );
    const newLine = {
      requestedItems: [],
      measurements: [], // Default measurement
      key: lineIndex,
    };
    const lines = [...materialRequestLines, newLine];
    setMaterialRequestLines(lines);
    lineIndex += 1;
  };

  const hasDuplicateMeasurementType = (
    measurements,
    measurementType,
    currentIndex
  ) => {
    return measurements.some((measurement, index) => {
      // Skip the current measurement being edited
      if (index === currentIndex) return false;
      return measurement.measurementType === measurementType;
    });
  };

  const handleAddMeasurement = (add, lineKey) => {
    const line = materialRequestLines.find((line) => line.key === lineKey);

    if (!line) {
      message.error("Request Line not found!");
      return;
    }

    const newMeasurement = {
      key: uuidv4(),
      measurementType: null,
      measurementValue: null,
    };

    // Check for duplicates before adding
    if (
      hasDuplicateMeasurementType(
        line.measurements,
        newMeasurement.measurementType,
        -1
      )
    ) {
      message.error("Duplicate measurement type is not allowed!");
      return;
    }

    const updatedLines = materialRequestLines.map((line) => {
      if (line.key === lineKey) {
        return {
          ...line,
          measurements: [...(line.measurements || []), newMeasurement], // Add the new measurement
        };
      }
      return line;
    });

    setMaterialRequestLines(updatedLines);

    add(newMeasurement);
  };



  const handleRemoveRequestLine = (line) => {
    //console.log("Remove:", materialRequestLines);
    const lines = [...materialRequestLines];
    if (lines.includes(line)) {
      lines.splice(lines.indexOf(line), 1);
      setMaterialRequestLines(lines);
    }
  };

  const handleSaveRequestLine = (e, key) => {
    e.persist();
    const target = getRowBykey(key) || {};
    /* if (
      !target.stockType ||
      !target.measurements ||
      target.measurements.length === 0
    ) {
      message.error("Please complete the requested line details.");
      e.target.focus();
      return;
    } */
    if (!target.stockType) {
      message.error("Please select a material type.");
      e.target.focus();
      return;
    }

    if (!target.measurements || target.measurements.length === 0) {
      message.error("Please add at least one measurement.");
      e.target.focus();
      return;
    }

    const invalidMeasurements = target.measurements.some((measurement) => {
      return !measurement.measurementType || !measurement.measurementValue;
    });

    if (invalidMeasurements) {
      message.error(
        "Please provide valid measurement type and value for all measurements."
      );
      e.target.focus();
      return;
    }
    handleAddRequestLine();
  };

  const getRowBykey = (key, newData) => {
    return (materialRequestLines || newData).filter(
      (item) => item.key === key
    )[0];
  };

  const validateRequestLines = (lines) => {
    if (!lines || lines.length === 0) {
      return false;
    }
    return lines.every((line) => {
      return (
        line.stockType &&
        line.measurements &&
        line.measurements.length > 0 &&
        !line.measurements.some((measurement) => {
          return !measurement.measurementType || !measurement.measurementValue;
        })
      );
    });
  };

  const handleFormSumbit = () => {
    form
      .validateFields()
      .then((values) => {
        if (validateRequestLines(materialRequestLines)) {
          setIsLoading(true);

          values.requestDate = moment(values.requestDate).format(
            "YYYY-MM-DD HH:mm:ss"
          );
          let requestLines = [];

          /* materialRequestLines.forEach((line) => {
            let requestLine = {};
            requestLine.stockType = { id: line.stockType.id };
            requestLine.measurements = line.measurements.map((measurement) => ({
              measurementType: { id: measurement.measurementType },
              measurementValue: measurement.measurementValue,
            }));
            requestLines.push(requestLine);
          }); */
          materialRequestLines.forEach((line) => {
            let requestLine = {};
            requestLine.stockType = { id: line.stockType.id };
            requestLine.measurements = line.measurements.map((measurement) => ({
              measurementType: { id: measurement.measurementType },
              measurementValue: measurement.measurementValue,
            }));
            requestLines.push(requestLine);
          });
          values.requestLines = requestLines;

          // console.log("MR: ", values);
          // console.log("MR: ", JSON.stringify(values));
          materialRequestService
            .create(values)
            .then(() => {
              setIsLoading(false);
              history.push(".");
            })
            .catch((error) => {
              setIsLoading(false);
              setError(error);
            });
        } else {
          message.error(
            "Please complete all the request lines with valid details"
          );
        }
      })
      .catch((error) => {
        //message.error(`${error}`);
      });
  };

  const onCancel = () => {
    history.push(".");
  };

  const getLineColumns = () => {
    let columns = [];
    columns.push({
      title: "Material Type",
      dataIndex: "stockType",
      key: "stockTypeCode",
      render: (text, record) => {
        return (
          <Form.Item
            //name={["stockType", record.key, "id"]}
            name={`stockType[${record.key}]`}
            key={record.key}
            style={{ marginBottom: "0" }}
            rules={[{ required: true, message: "Required." }]}
            initialValue={text}
            hasFeedback
          >
            <Select
              placeholder="Please select"
              allowClear
              showSearch
              optionFilterProp="children"
              onChange={(e) => handleFieldChange(e, "stockType", record.key)}
            >
              {materialTypes.length > 0 &&
                materialTypes.map((t) => (
                  <Option value={t.id} key={t.id}>
                    {t.code} ({t.name})
                  </Option>
                ))}
            </Select>
          </Form.Item>
        );
      },
    });
    columns.push({
      title: "Measurements",
      key: "measurements",
      render: (text, record) => {
        return (
          <Form.List name={`measurements[${record.key}]`}>
            {(fields, { add, remove }) => (
              <>
                {fields.map((field, index) => (
                  <Space
                    key={field.key}
                    style={{ display: "flex", marginBottom: 8 }}
                    align="baseline"
                  >
                    <Form.Item
                      {...field}
                      name={[field.name, "measurementType"]}
                      rules={[
                        { required: true, message: "Required." },
                        {
                          validator: (_, value) => {
                            const line = materialRequestLines.find(
                              (line) => line.key === record.key
                            );
                            if (
                              hasDuplicateMeasurementType(
                                line.measurements,
                                value,
                                field.name
                              )
                            ) {
                              return Promise.reject(
                                "Duplicate measurement type is not allowed!"
                              );
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <Select
                        placeholder="Measurement Type"
                        style={{ width: 120 }}
                        onChange={(value) =>
                          handleFieldChange(
                            value,
                            "measurementType",
                            record.key,
                            index
                          )
                        }
                      >
                        {measurementTypes.map((t) => (
                          <Option value={t.id} key={t.id}>
                            {t.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                    <Form.Item
                      {...field}
                      name={[field.name, "measurementValue"]}
                      rules={[{ required: true, message: "Required." }]}
                    >
                      <InputNumber
                        placeholder="Value"
                        min={0}
                        onChange={(value) =>
                          handleFieldChange(
                            value,
                            "measurementValue",
                            record.key,
                            index
                          )
                        }
                      />
                    </Form.Item>
                    <CloseOutlined onClick={() => remove(field.name)} />
                  </Space>
                ))}
                <Button
                  type="dashed"
                  onClick={() => handleAddMeasurement(add, record.key)}
                  block
                  disabled={!form.getFieldValue(["stockClass", "id"])}
                  icon={<PlusOutlined />}
                >
                  Add Measurement
                </Button>
              </>
            )}
          </Form.List>
        );
      },
    });
    /* columns.push({
      title: "Quantity",
      key: "quantity",
      align: "right",
      render: (text, record) => {
        return (
          <Form.Item
            name={`quantity[${record.key}]`}
            key={record.key}
            style={{ marginBottom: "0" }}
            rules={[{ required: true, message: "Required." }]}
            hasFeedback
          >
            <InputNumber
              placeholder="No of Pallet"
              min={record._id ? record.quantity : 0}
              onChange={(e) => handleFieldChange(e, "quantity", record.key)}
            />
          </Form.Item>
        );
      },
    }); */
    /* columns.push({
      title: "Measurements",
      key: "measurements",
      render: (text, record) => {
        return (
          <Form.List name={`measurements[${record.key}]`}>
            {(fields, { add, remove }) => (
              <>
                {fields.map((field, index) => (
                  <Space
                    key={field.key}
                    style={{ display: "flex", marginBottom: 8 }}
                    align="baseline"
                  >
                    <Form.Item
                      {...field}
                      name={[field.name, "measurementType"]}
                      fieldKey={[field.fieldKey, "measurementType"]}
                      rules={[{ required: true, message: "Required." }]}
                    >
                      <Select
                        placeholder="Measurement Type"
                        style={{ width: 120 }}
                      >
                        {measurementTypes.map((t) => (
                          <Option value={t.id} key={t.id}>
                            {t.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                    <Form.Item
                      {...field}
                      name={[field.name, "value"]}
                      fieldKey={[field.fieldKey, "value"]}
                      rules={[{ required: true, message: "Required." }]}
                    >
                      <InputNumber placeholder="Value" />
                    </Form.Item>
                    {index === 0 ? (
                      <Button
                        type="dashed"
                        onClick={() => add()}
                        icon={<PlusOutlined />}
                      />
                    ) : (
                      <CloseOutlined onClick={() => remove(field.name)} />
                    )}
                  </Space>
                ))}
              </>
            )}
          </Form.List>
        );
      },
    }); */
    /* columns.push({
      title: "Measurement",
      dataIndex: "measurementType",
      key: "measurementType",
      width: "10%",
      render: (text, record) => {
        return (
          <Form.Item
            name={`measurementType[${record.key}]`}
            key={record.key}
            style={{ marginBottom: "0" }}
            rules={[{ required: true, message: "Required." }]}
            initialValue={text}
            hasFeedback
          >
            <Select
              placeholder="Please select"
              allowClear
              showSearch
              optionFilterProp="children"
              onChange={(e) =>
                handleFieldChange(e, "measurementType", record.key)
              }
            >
              {measurementTypes.length > 0 &&
                measurementTypes.map((t) => (
                  <Option value={t.id} key={t.id}>
                    {t.name}
                  </Option>
                ))}
            </Select>
          </Form.Item>
        );
      },
    }); */
    columns.push({
      title: "",
      key: "operation",
      render: (text, record) => (
        <Space size="small">
          <Button
            type="primary"
            onClick={(e) => {
              e.stopPropagation();
              handleRemoveRequestLine(record);
            }}
            icon={<CloseOutlined />}
            style={{ marginLeft: 5 }}
          ></Button>
          <Button
            type="primary"
            onClick={(e) => {
              e.preventDefault();
              handleSaveRequestLine(e, record.key);
            }}
            style={{ padding: 5, fontSize: 12 }}
          >
            +More
          </Button>
        </Space>
      ),
    });

    return columns;
  };

  const getActionButtons = () => {
    let buttons = [];
    buttons.push(
      <Popconfirm
        title="Are you sure to add the changes?"
        onConfirm={handleFormSumbit}
        key="add-confirm"
        disabled={materialRequestLines.length === 0}
      >
        <Button
          key="1"
          type="primary"
          htmlType="submit"
          loading={isLoading}
          disabled={materialRequestLines.length === 0}
        >
          Save
        </Button>
      </Popconfirm>
    );

    buttons.push(
      <Button key="2" htmlType="button" onClick={onCancel}>
        Cancel
      </Button>
    );

    return buttons;
  };

  return (
    <div>
      <PageHeader
        title={
          <Breadcrumb>
            <Breadcrumb.Item>
              <Link to="/dashboard">Dashboard</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link to="/material-requests">Material Request Search</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link to="/material-requests/add">New Material Request</Link>
            </Breadcrumb.Item>
          </Breadcrumb>
        }
        extra={getActionButtons()}
      >
        <Page inner error={error}>
          <Form
            layout="vertical"
            form={form}
            name="form-create"
            requiredMark={false}
          >
            <Row gutter={24}>
              <Col span={6}>
                <Form.Item
                  name={["store", "id"]}
                  label="Store"
                  rules={[{ required: true, message: "Store is required" }]}
                >
                  <Select
                    placeholder="Please select"
                    allowClear
                    onChange={getStoreAddresses}
                    loading={isLoadingStores}
                  >
                    {stores.length > 0 &&
                      stores.map((t) => (
                        <Option value={t.id} key={t.id}>
                          {t.name}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name={["customer", "id"]}
                  label="Customer"
                  rules={[{ required: true, message: "Customer is required" }]}
                >
                  <Select
                    placeholder="Please select"
                    allowClear
                    onChange={getCustomerAddresses}
                    loading={isLoadingCustomers}
                  >
                    {customers.length > 0 &&
                      customers.map((t) => (
                        <Option value={t.id} key={t.id}>
                          {t.name}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name="requestDate"
                  label="Request Date"
                  rules={[
                    { required: true, message: "Request date is required" },
                  ]}
                  initialValue={moment()}
                >
                  <DatePicker
                    placeholder="Request date"
                    format="YYYY-MM-DD"
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name={["stockClass", "id"]}
                  label="Stock Classification"
                  rules={[
                    {
                      required: true,
                      message: "Stock classification is required",
                    },
                  ]}
                >
                  <Select
                    placeholder="Please select"
                    allowClear
                    onChange={getMaterialTypesBasedOnClassification}
                    loading={isLoadingStockClassification}
                  >
                    {stockClassifications.length > 0 &&
                      stockClassifications.map((t) => (
                        <Option value={t.id} key={t.id}>
                          {t.name}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={6}></Col>
            </Row>
            <Row gutter={24}>
              <Col span={6}>
                <Form.Item
                  name={["storeAddress", "id"]}
                  label="Delivery From"
                  rules={[
                    {
                      required: true,
                      message: "Delivery address is required",
                    },
                  ]}
                >
                  <Select
                    placeholder="Please select"
                    allowClear
                    loading={isLoadingStoreAddresses}
                  >
                    {storeAddresses.length > 0 &&
                      storeAddresses.map((t) => (
                        <Option value={t.id} key={t.id}>
                          {t.address}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name={["deliveryLocation", "id"]}
                  label="Deliver To"
                  rules={[
                    { required: true, message: "Delivery from is required" },
                  ]}
                >
                  <Select
                    placeholder="Please select"
                    allowClear
                    loading={isLoadingCustomerAddresses}
                  >
                    {customerAddresses.length > 0 &&
                      customerAddresses.map((t) => (
                        <Option value={t.id} key={t.id}>
                          {t.address}
                        </Option>
                      ))}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name="contactPersonName"
                  label="Contact Person Name"
                  rules={[
                    { required: true, message: "Contact persion is required" },
                  ]}
                >
                  <Input placeholder="Contact persion" />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item
                  name="contactNumber"
                  label="Contact No:"
                  rules={[
                    { required: true, message: "Contact No. is required" },
                  ]}
                >
                  <Input placeholder="Contact number" />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={24}>
              <Col span={24}>
                <Form.Item name="remark" label="Remark">
                  <TextArea />
                </Form.Item>
              </Col>
            </Row>

            {materialRequestLines.length > 0 && (
              <Table
                bordered
                pagination={false}
                columns={getLineColumns()}
                dataSource={materialRequestLines}
                size="small"
                summary={(pageData) => {
                  let rollSummary = {
                    totalItems: 0,
                    totalQty: 0,
                    uom: "",
                    measurementType: null,
                  };
                  let boxSummary = {
                    totalItems: 0,
                    totalQty: 0,
                    uom: "",
                    measurementType: null,
                  };
                  let spoolSummary = {
                    totalItems: 0,
                    totalQty: 0,
                    uom: "",
                    measurementType: null,
                  };
                  let boxPcsSummary = {
                    totalItems: 0,
                    totalQty: 0,
                    uom: "",
                    measurementType: null,
                  };
                  let boxNosSummary = {
                    totalItems: 0,
                    totalQty: 0,
                    uom: "",
                    measurementType: null,
                  };
                  let boxConeSummary = {
                    totalItems: 0,
                    totalQty: 0,
                    uom: "",
                    measurementType: null,
                  };
                  let bundlesSummary = {
                    totalItems: 0,
                    totalQty: 0,
                    uom: "",
                    measurementType: null,
                  };
                  let defaultSummary = {
                    totalItems: 0,
                    totalQty: 0,
                    uom: "",
                    measurementType: null,
                  };
                  const groupByPackingType = Utils.groupBy(
                    pageData,
                    (line) =>
                      line.stockType &&
                      line.stockType.packingType &&
                      line.stockType.packingType.name
                  );

                  groupByPackingType.forEach((value, key) => {
                    if (key === "ROLL") {
                      value.forEach(
                        ({ totalItems, quantity, measurementType }) => {
                          rollSummary.totalItems += totalItems;
                          rollSummary.totalQty += quantity;
                          rollSummary.measurementType = measurementType;
                        }
                      );
                    } else if (key === "BOX") {
                      value.forEach(
                        ({ totalItems, quantity, measurementType }) => {
                          boxSummary.totalItems += totalItems;
                          boxSummary.totalQty += quantity;
                          boxSummary.measurementType = measurementType;
                        }
                      );
                    } else if (key === "SPOOL") {
                      value.forEach(
                        ({ totalItems, quantity, measurementType }) => {
                          spoolSummary.totalItems += totalItems;
                          spoolSummary.totalQty += quantity;
                          spoolSummary.measurementType = measurementType;
                        }
                      );
                    } else if (key === "BOX_PCS") {
                      value.forEach(
                        ({ totalItems, quantity, measurementType }) => {
                          boxPcsSummary.totalItems += totalItems;
                          boxPcsSummary.totalQty += quantity;
                          boxPcsSummary.measurementType = measurementType;
                        }
                      );
                    } else if (key === "BOX_NOS") {
                      value.forEach(
                        ({ totalItems, quantity, measurementType }) => {
                          boxNosSummary.totalItems += totalItems;
                          boxNosSummary.totalQty += quantity;
                          boxNosSummary.measurementType = measurementType;
                        }
                      );
                    } else if (key === "BOX_CONE") {
                      value.forEach(
                        ({ totalItems, quantity, measurementType }) => {
                          boxConeSummary.totalItems += totalItems;
                          boxConeSummary.totalQty += quantity;
                          boxConeSummary.measurementType = measurementType;
                        }
                      );
                    } else if (key === "BUNDLES") {
                      value.forEach(
                        ({ totalItems, quantity, measurementType }) => {
                          bundlesSummary.totalItems += totalItems;
                          bundlesSummary.totalQty += quantity;
                          bundlesSummary.measurementType = measurementType;
                        }
                      );
                    } else {
                      value.forEach(
                        ({ totalItems, quantity, measurementType }) => {
                          defaultSummary.totalItems += totalItems;
                          defaultSummary.totalQty += quantity;
                          defaultSummary.measurementType = measurementType;
                        }
                      );
                    }
                  });
                  measurementTypes.forEach((item) => {
                    if (item.id === rollSummary.measurementType) {
                      rollSummary.uom = item.name;
                    }
                    if (item.id === boxSummary.measurementType) {
                      boxSummary.uom = item.name;
                    }
                    if (item.id === spoolSummary.measurementType) {
                      spoolSummary.uom = item.name;
                    }
                    if (item.id === boxPcsSummary.measurementType) {
                      boxPcsSummary.uom = item.name;
                    }
                    if (item.id === boxNosSummary.measurementType) {
                      boxNosSummary.uom = item.name;
                    }
                    if (item.id === boxConeSummary.measurementType) {
                      boxConeSummary.uom = item.name;
                    }
                    if (item.id === bundlesSummary.measurementType) {
                      bundlesSummary.uom = item.name;
                    }
                    if (item.id === defaultSummary.measurementType) {
                      defaultSummary.uom = item.name;
                    }
                  });

                  return (
                    <>
                      {rollSummary.totalItems > 0 && (
                        <Table.Summary.Row>
                          <Table.Summary.Cell colSpan={2}>
                            <Text strong>Total - ROLL</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell align="right">
                            <Text strong>
                              {!isNaN(rollSummary.totalItems) &&
                                rollSummary.totalItems}
                            </Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell align="right">
                            <Text strong>
                              {!isNaN(rollSummary.totalQty) &&
                                rollSummary.totalQty}
                            </Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell>
                            <Text strong>{rollSummary.uom}</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell />
                        </Table.Summary.Row>
                      )}
                      {boxSummary.totalItems > 0 && (
                        <Table.Summary.Row>
                          <Table.Summary.Cell colSpan={2}>
                            <Text strong>Total - BOX</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell align="right">
                            <Text strong>
                              {!isNaN(boxSummary.totalItems) &&
                                boxSummary.totalItems}
                            </Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell align="right">
                            <Text strong>
                              {!isNaN(boxSummary.totalQty) &&
                                boxSummary.totalQty}
                            </Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell>
                            <Text strong>{boxSummary.uom}</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell />
                        </Table.Summary.Row>
                      )}
                      {spoolSummary.totalItems > 0 && (
                        <Table.Summary.Row>
                          <Table.Summary.Cell colSpan={2}>
                            <Text strong>Total - SPOOL</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell align="right">
                            <Text strong>
                              {!isNaN(spoolSummary.totalItems) &&
                                spoolSummary.totalItems}
                            </Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell align="right">
                            <Text strong>
                              {!isNaN(spoolSummary.totalQty) &&
                                spoolSummary.totalQty}
                            </Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell>
                            <Text strong>{spoolSummary.uom}</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell />
                        </Table.Summary.Row>
                      )}
                      {boxPcsSummary.totalItems > 0 && (
                        <Table.Summary.Row>
                          <Table.Summary.Cell colSpan={2}>
                            <Text strong>Total - BOX(PCS)</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell align="right">
                            <Text strong>
                              {!isNaN(boxPcsSummary.totalItems) &&
                                boxPcsSummary.totalItems}
                            </Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell align="right">
                            <Text strong>
                              {!isNaN(boxPcsSummary.totalQty) &&
                                boxPcsSummary.totalQty}
                            </Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell>
                            <Text strong>{boxPcsSummary.uom}</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell />
                        </Table.Summary.Row>
                      )}
                      {boxNosSummary.totalItems > 0 && (
                        <Table.Summary.Row>
                          <Table.Summary.Cell colSpan={2}>
                            <Text strong>Total - BOX(NOS)</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell align="right">
                            <Text strong>
                              {!isNaN(boxNosSummary.totalItems) &&
                                boxNosSummary.totalItems}
                            </Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell align="right">
                            <Text strong>
                              {!isNaN(boxNosSummary.totalQty) &&
                                boxNosSummary.totalQty}
                            </Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell>
                            <Text strong>{boxNosSummary.uom}</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell />
                        </Table.Summary.Row>
                      )}
                      {boxConeSummary.totalItems > 0 && (
                        <Table.Summary.Row>
                          <Table.Summary.Cell colSpan={2}>
                            <Text strong>Total - BOX(CONE)</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell align="right">
                            <Text strong>
                              {!isNaN(boxConeSummary.totalItems) &&
                                boxConeSummary.totalItems}
                            </Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell align="right">
                            <Text strong>
                              {!isNaN(boxConeSummary.totalQty) &&
                                boxConeSummary.totalQty}
                            </Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell>
                            <Text strong>{boxConeSummary.uom}</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell />
                        </Table.Summary.Row>
                      )}
                      {bundlesSummary.totalItems > 0 && (
                        <Table.Summary.Row>
                          <Table.Summary.Cell colSpan={2}>
                            <Text strong>Total - BUNDLES</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell align="right">
                            <Text strong>
                              {!isNaN(bundlesSummary.totalItems) &&
                                bundlesSummary.totalItems}
                            </Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell align="right">
                            <Text strong>
                              {!isNaN(bundlesSummary.totalQty) &&
                                bundlesSummary.totalQty}
                            </Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell>
                            <Text strong>{bundlesSummary.uom}</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell />
                        </Table.Summary.Row>
                      )}
                      {defaultSummary.totalItems > 0 && (
                        <Table.Summary.Row>
                          <Table.Summary.Cell colSpan={2}>
                            <Text strong>Total</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell align="right">
                            <Text strong>
                              {!isNaN(defaultSummary.totalItems) &&
                                defaultSummary.totalItems}
                            </Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell align="right">
                            <Text strong>
                              {!isNaN(defaultSummary.totalQty) &&
                                defaultSummary.totalQty}
                            </Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell>
                            <Text strong>{defaultSummary.uom}</Text>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell />
                        </Table.Summary.Row>
                      )}
                    </>
                  );
                }}
              />
            )}
            {materialRequestLines.length === 0 && (
              <Button
                style={{
                  width: "100%",
                  marginTop: 16,
                  marginBottom: 8,
                  color: "white",
                  backgroundColor: "#13c2c2",
                }}
                type="dashed"
                onClick={handleAddRequestLine}
                icon={<PlusOutlined />}
                disabled={!form.getFieldValue(["stockClass", "id"])}
              >
                Add Item
              </Button>
            )}
          </Form>
        </Page>
      </PageHeader>
    </div>
  );
}

export default Add;
